/**
 * Display-timezone formatting. Every timestamp the operator sees should render in
 * the zone they picked in the top bar — use these instead of `Date#toLocale*`.
 *
 *   const t = useFormatTime();        // call at the top of a component
 *   <span>{t.datetime(row.created_at)}</span>
 *   <span>{t.time(quote.ts)}</span>
 *   // or, directly in JSX:
 *   <Time value={row.created_at} fmt="datetime" />
 *
 * Wire times are UTC ISO-8601; these convert to the SELECTED display tz (or the
 * system zone if the operator hasn't picked one). Reactive: changing the picker
 * re-renders everything.
 */
'use client';

import * as React from 'react';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import { DATETIME_PATTERNS } from '@/lib/formatters/date';
import { useDisplayTzStore } from './store';

export function systemTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** The effective IANA tz the operator is viewing in (their pick, else the system zone). */
export function useDisplayTimezone(): string {
  const selected = useDisplayTzStore((s) => s.selected);
  return selected ?? systemTimezone();
}

type Val = string | number | Date | null | undefined;

// Named formats → date.ts patterns. Keep this the ONE place display formats live.
const FMT = {
  datetime: DATETIME_PATTERNS.minutes,      // 12 May 2026, 09:13 IST
  datetimeSec: DATETIME_PATTERNS.full,      // 12 May 2026, 09:13:45 IST
  time: DATETIME_PATTERNS.time,             // 09:13:45
  timeShort: DATETIME_PATTERNS.timeShort,   // 09:13
  date: DATETIME_PATTERNS.isoDate,          // 2026-05-12
  monthDay: DATETIME_PATTERNS.monthDay,     // 12 May
} as const;
export type TimeFmt = keyof typeof FMT;

export function formatInTz(v: Val, tz: string, fmt: TimeFmt = 'datetime'): string {
  if (v == null || v === '') {return '—';}
  try {
    const d = typeof v === 'string' ? parseISO(v) : new Date(v);
    if (Number.isNaN(d.getTime())) {return '—';}
    return formatInTimeZone(d, tz, FMT[fmt]);
  } catch {
    return '—';
  }
}

/** Formatter functions bound to the current display tz. Call at component top. */
export function useFormatTime() {
  const tz = useDisplayTimezone();
  return React.useMemo(
    () => ({
      tz,
      datetime: (v: Val) => formatInTz(v, tz, 'datetime'),
      datetimeSec: (v: Val) => formatInTz(v, tz, 'datetimeSec'),
      time: (v: Val) => formatInTz(v, tz, 'time'),
      timeShort: (v: Val) => formatInTz(v, tz, 'timeShort'),
      date: (v: Val) => formatInTz(v, tz, 'date'),
      monthDay: (v: Val) => formatInTz(v, tz, 'monthDay'),
    }),
    [tz],
  );
}

/** Drop-in for rendering a timestamp in the selected tz (with a full-precision title). */
export function Time({
  value, fmt = 'datetime', className,
}: { value: Val; fmt?: TimeFmt; className?: string }) {
  const tz = useDisplayTimezone();
  return (
    <span className={className} title={formatInTz(value, tz, 'datetimeSec')}>
      {formatInTz(value, tz, fmt)}
    </span>
  );
}
